import { apiImageAnalysis } from 'apis/SensorApi';
import { getUrl, list } from 'aws-amplify/storage';
import FanLoadingIcon from 'components/FanLoadingIcon';
import { useDialog } from 'context/DialogContext';
import { useGeneralSetting } from 'context/GeneralSettingContext';
import { useSensors } from 'context/SensorsContext';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import './CameraDisplayer.css';
import { retrieveDateTimeFromVideoKey, retrieveLabelFromNameAndDirection } from 'pages/Base/helpers';
import { useAuth } from 'context/AuthContext';

// Custom hooks
export const useImageSource = (imageProps) => {
  const [imageSrc, setImageSrc] = useState('');

  useEffect(() => {
    const fetchImageData = async () => {
      if (imageProps?.key) {
        try {
          const url = await getUrl({ key: imageProps.key });
          setImageSrc(url.url.toString());
        } catch (error) {
          setImageSrc('');
        }
      }
    };

    fetchImageData();
  }, [imageProps?.key]);

  useEffect(() => {
    if (imageProps?.src) {
      setImageSrc(imageProps.src);
    }
  }, [imageProps?.src]);

  const handleImageError = useCallback(() => {
    setImageSrc('');
  }, []);

  return { imageSrc, handleImageError };
};

const useVideoSource = (sensorId, cameraNumber, isActive, onVideoLoaded) => {
  const [videoFileName, setVideoFileName] = useState(undefined);
  const [intervalId, setIntervalId] = useState(null);

  useEffect(() => {
    if (sensorId && cameraNumber && isActive) {
      fetchVideoData()
        .then(result => {
          if (result.length === 0) {
            onVideoLoaded?.();
          }
        })
    }
  }, [sensorId, cameraNumber, isActive]);

  const listVideoFiles = useCallback(async () => {
    try {
      const response = await list({
        prefix: `camera-videos/${sensorId}_${cameraNumber}`,
        listAll: true,
      });

      return response.items ? response.items.map(item => item.key.split('/')[2]) : [];
    } catch (error) {
      return [];
    }
  }, [sensorId, cameraNumber]);

  const fetchVideoData = useCallback(async () => {
    const listVideoFile = await listVideoFiles();
    const listVideoFileName = listVideoFile.filter(name => !!name);
    if (listVideoFileName.length === 0) return [];

    setVideoFileName((prev) => {
      const currentIndex = prev ? listVideoFileName.indexOf(prev) : -1;
      return (currentIndex === -1 || currentIndex === listVideoFileName.length - 1)
        ? listVideoFileName[0]
        : listVideoFileName[currentIndex + 1];
    });
    return listVideoFileName;
  }, [listVideoFiles]);

  const handleVideoEnded = useCallback(() => {
    if (intervalId) {
      clearInterval(intervalId);
    }
    fetchVideoData();

    // Start checking for new videos every 10 seconds
    const newIntervalId = setInterval(() => {
      fetchVideoData();
    }, 10000);
    setIntervalId(newIntervalId);
  }, [fetchVideoData, intervalId]);

  const handleVideoStarted = useCallback(() => {
    // Clear interval when a new video starts playing
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
  }, [intervalId]);

  const handleVideoError = useCallback(async () => {
    fetchVideoData();
  }, [fetchVideoData]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [intervalId]);

  return {
    videoKey: `camera-videos/${sensorId}_${cameraNumber}/${videoFileName}`,
    handleVideoEnded,
    handleVideoError,
    handleVideoStarted
  };
};

// Main component
export default function CameraDisplayer({
  targetSensor,
  cameraNumber = 1,
  isDisplayVideo = false,
  isImageAnalysis = false,
  isInList = false,
  isCameraWindow = false,
  label,
  popupText,
  style,
  onLoad,
  onClick,
  subComponent,
  imageProps = {},
  zoomImageProps = {},
  videoProps = {
    lazy: false,
    isActive: true,
    onVideoLoaded: () => { },
  },
  buttonStatusProps = {},
}) {
  const sensorId = targetSensor?.id;
  const { imageSrc, handleImageError } = useImageSource(imageProps);
  const { imageSrc: zoomImageSrc } = useImageSource(zoomImageProps);
  const { videoKey, handleVideoEnded, handleVideoError, handleVideoStarted } = useVideoSource(sensorId, cameraNumber, videoProps.isActive, videoProps.onVideoLoaded);
  const handleClick = useCallback(() => {
    onClick?.({ id: sensorId }, { cameraNumber });
  }, [onClick, sensorId, cameraNumber]);

  const handleSubComponent = useCallback((size) => {
    return subComponent?.({ src: zoomImageSrc, cameraNumber, size, videoKey });
  }, [subComponent, zoomImageSrc, cameraNumber]);

  const itemProps = {
    imageProps: {
      src: imageSrc,
      onError: handleImageError,
    },
    videoProps: {
      targetSensor,
      cameraNumber,
      useFor: videoProps?.useFor || '',
      lazy: videoProps.lazy,
      isActive: videoProps.isActive,
      videoKey,
      onVideoLoadedData: videoProps.onVideoLoaded,
      onVideoEnded: handleVideoEnded,
      onVideoError: handleVideoError,
      onVideoStarted: handleVideoStarted,
    },
    buttonStatusProps: {
      sensorId,
      cameraNumber,
      status: buttonStatusProps.status,
      toolTipContent: buttonStatusProps.toolTipContent,
    },
    isDisplayVideo,
    isImageAnalysis,
    isInList,
    isCameraWindow,
    label,
    popupText,
    style,
    onLoad,
    onClick: handleClick,
    subComponent: handleSubComponent,
  };

  return <CameraItem {...itemProps} />;
}

// Separated Item component for better modularity
const CameraItem = ({
  imageProps,
  videoProps,
  buttonStatusProps,
  isDisplayVideo,
  isImageAnalysis,
  isInList,
  isCameraWindow,
  label,
  style,
  popupText,
  subComponent,
  onClick
}) => {
  const [size, setSize] = useState({ width: 0, height: 0 });
  const { generalSetting } = useGeneralSetting();

  const handleLoad = useCallback((e) => {
    setSize({ width: e.target.offsetWidth, height: e.target.offsetHeight });
  }, []);

  const getButtonPosition = () => {
    if (isInList) return 20;
    return generalSetting?.menuType === 'drawer' ? -90 : -50;
  };



  return (
    <div className={`camera_diplayer_item_container ${videoProps.useFor === 'cameraWindow' ? 'w-250' : ''}`} 
    style={style}
    >
      {/* {isCameraWindow && videoProps.targetSensor.data.typeCamera === "TWIN" && (
        <div className='map-page_flex-row map_page_card-info' style={{ paddingBottom: 10 }}>
          <p className='normal_text card-info_value'>
            カメラユニット{videoProps.cameraNumber}
          </p>
        </div>
      )} */}
      {isImageAnalysis && buttonStatusProps.status && (
        <ButtonStatus
          {...buttonStatusProps}
          style={{ top: getButtonPosition() }}
        />
      )}
      <div className='camera_diplayer_item_inner' onClick={onClick}>
        <div className='camera_diplayer_item_overlay' />
        {popupText && <div className='camera_diplayer_item_popup'>{popupText}</div>}
        {isDisplayVideo ? (
          <VideoPlayer {...videoProps} label={label} onLoad={handleLoad} onClick={onClick} />
        ) : (
          <ImageDisplay {...imageProps} label={label} onLoad={handleLoad} />
        )}
        {label && <div className='camera_diplayer_item_label'>{label}</div>}
      </div>
      {subComponent?.(size)}
    </div>
  );
};

// Separated Image component
const ImageDisplay = ({ src, onLoad, onError, useFor }) => {
  if (!src) {
    return <div className={`camera_diplayer_item_content_fallback ${useFor === 'cameraWindow' ? 'w-250' : ''}`} />;
  }

  return (
    <img
      className={`camera_diplayer_item_content ${useFor === 'cameraWindow' ? 'w-250' : ''}`}
      src={src}
      alt=''
      onLoad={onLoad}
      onError={onError}
    />
  );
};

const VideoPlayer = (props) => {
  const {
    targetSensor,
    cameraNumber,
    videoKey,
    useFor,
    lazy = false,
    isActive = true,
    onVideoLoadedData,
    onVideoEnded,
    onVideoError,
    onVideoStarted,
    onLoad,
    onClick
  } = props;
  const [src, setSrc] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const { generalSetting } = useGeneralSetting();

  const isHideCotrols = ['cameraWindow', 'split', 'base'].includes(useFor);

  useEffect(() => {
    const fetchVideoUrl = async () => {
      if (!videoKey || videoKey.includes('undefined')) return;

      try {
        setIsLoading(true);
        const url = await getUrl({
          key: videoKey,
          options: {
            validateObjectExistence: true
          }
        });
        setSrc(url.url.toString());
      } catch (error) {
        setSrc('');
        setIsLoading(false);
        onVideoError?.();
      }
    };

    fetchVideoUrl();
  }, [videoKey]);

  const handleVideoClick = (e) => {
    e.stopPropagation();
    onClick?.();
  };

  const handleVideoLoadedData = () => {
    setIsLoading(false);
    onVideoLoadedData?.();
  }

  const handleVideoPlay = () => {
    onVideoStarted?.();
  };

  const handleVideoEnded = () => {
    onVideoEnded?.();
  }

  const handleVideoError = () => {
    onVideoError?.();
  };

  const dateTimeLabel = retrieveDateTimeFromVideoKey(videoKey);
  const nameLabel = retrieveLabelFromNameAndDirection(targetSensor, cameraNumber)
  const locationLabel = targetSensor?.location || ''

  return (
    <div className={`video-container ${useFor === 'cameraWindow' ? 'w-250' : ''}`}>
      {isLoading && isHideCotrols && (
        <div className="video-loading-overlay">
          <FanLoadingIcon />
        </div>
      )}
      <video
        muted
        className={`camera_diplayer_item_content ${useFor === 'cameraWindow' ? 'w-250' : ''}`}
        preload={lazy ? (isActive ? "auto" : "none") : "auto"}
        src={lazy ? (isActive ? src : undefined) : src}
        controls={!isHideCotrols}
        loop={false}
        autoPlay
        onEnded={handleVideoEnded}
        onError={handleVideoError}
        onPlay={handleVideoPlay}
        onLoadedData={handleVideoLoadedData}
        playsInline
        webkitplaysinline="true"
        onLoadedMetadata={onLoad}
        onClick={handleVideoClick}
      />
      {/* Label overlay */}
      {generalSetting?.videoOverlay && dateTimeLabel && locationLabel && nameLabel && <>
        <span className="video-label video-label-top-left">
          {locationLabel}
        </span>
        <span className="video-label video-label-top-right">
          {dateTimeLabel}
        </span>
        <span className="video-label video-label-bottom-center">
          {nameLabel}
        </span>
      </>}
    </div>
  );
};

const ButtonStatus = ({ sensorId, cameraNumber, status, toolTipContent, style }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { error } = useDialog();
  const { reload } = useSensors();

  const handleClick = useCallback(async () => {
    if (isLoading || !status) return;

    try {
      setIsLoading(true);
      const res = await apiImageAnalysis({ sensorId, cameraNumber });

      if (!res.success) {
        error('画像解析に失敗しました。');
        return;
      }

      reload();
    } catch (err) {
      console.error('Image analysis error:', err);
      error('画像解析に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, status, sensorId, cameraNumber, error, reload]);

  if (!status) return null;

  return (
    <div className='camera_diplayer_item_button_status_wrapper' style={style}>
      {!isLoading && toolTipContent && (
        <div className='camera_diplayer_item_button_status_tooltip_wrapper'>
          <div className='camera_diplayer_item_button_status_tooltip_content'>
            {toolTipContent}
          </div>
        </div>
      )}
      <button
        className='camera_diplayer_item_button_status'
        onClick={handleClick}
        disabled={isLoading}
        style={{
          backgroundColor: status.color,
          opacity: isLoading ? 0.6 : 1
        }}
      >
        {isLoading ? (
          <FanLoadingIcon strokeWidth={2} size={20} color='#fff' />
        ) : (
          status.value
        )}
      </button>
    </div>
  );
};

// PropTypes
CameraDisplayer.propTypes = {
  targetSensor: PropTypes.object.isRequired,
  cameraNumber: PropTypes.number,
  isDisplayVideo: PropTypes.bool,
  isImageAnalysis: PropTypes.bool,
  isInList: PropTypes.bool,
  label: PropTypes.string,
  popupText: PropTypes.string,
  style: PropTypes.object,
  onLoad: PropTypes.func,
  onClick: PropTypes.func,
  subComponent: PropTypes.func,
  imageProps: PropTypes.shape({
    src: PropTypes.string,
    key: PropTypes.string,
  }),
  videoProps: PropTypes.shape({
    lazy: PropTypes.bool,
    isActive: PropTypes.bool,
    isLastInRow: PropTypes.bool,
    onVideoLoaded: PropTypes.func,
  }),
  buttonStatusProps: PropTypes.shape({
    status: PropTypes.shape({
      color: PropTypes.string,
      value: PropTypes.string,
    }),
    toolTipContent: PropTypes.string,
  })
};

CameraItem.propTypes = {
  imageProps: PropTypes.shape({
    src: PropTypes.string,
    onError: PropTypes.func,
  }),
  videoProps: PropTypes.shape({
    lazy: PropTypes.bool,
    isActive: PropTypes.bool,
    videoKey: PropTypes.string,
    onVideoEnded: PropTypes.func,
    onVideoError: PropTypes.func,
    onVideoLoadedData: PropTypes.func,
  }),
  buttonStatusProps: PropTypes.shape({
    sensorId: PropTypes.string,
    cameraNumber: PropTypes.number,
    status: PropTypes.shape({
      color: PropTypes.string,
      value: PropTypes.string,
    }),
    toolTipContent: PropTypes.string,
  }),
  style: PropTypes.object,
  isDisplayVideo: PropTypes.bool,
  isImageAnalysis: PropTypes.bool,
  isInList: PropTypes.bool,
  label: PropTypes.string,
  popupText: PropTypes.string,
  subComponent: PropTypes.func,
  onClick: PropTypes.func,
  onLoad: PropTypes.func,
};

ImageDisplay.propTypes = {
  src: PropTypes.string,
  onLoad: PropTypes.func,
  onError: PropTypes.func,
};

VideoPlayer.propTypes = {
  videoKey: PropTypes.string,
  onVideoEnded: PropTypes.func,
  onVideoError: PropTypes.func,
  onLoad: PropTypes.func,
  onClick: PropTypes.func,
};

ButtonStatus.propTypes = {
  sensorId: PropTypes.string.isRequired,
  cameraNumber: PropTypes.number,
  status: PropTypes.shape({
    color: PropTypes.string,
    value: PropTypes.string,
  }),
  toolTipContent: PropTypes.string,
  style: PropTypes.object,
};